// TRAC-975 — pure derivation of the emergency overlay's two decisions:
//
//   1. Should the overlay be up at all?           → emergencyOverlayActive()
//   2. What route to a person does it offer?      → overlayHumanRoute()
//
// Both are extracted from ChatInterface for the same reason bannerModel is
// (chatBanner.ts): the mapping is a small state machine and belongs in a test,
// not inside a 4,000-line component.
//
// Background. Until now the overlay offered a way through to a person only when
// its message was string-identical to RETRY_EXHAUSTED_MESSAGE. Auto-maintenance
// copy is different text, so the screen that says "our tire expert is
// temporarily unavailable" was a dead end — while `escalateToHuman` (a straight
// POST to /agent/escalate-to-human, no agent involved) was working the whole
// time. On 2026-09-04 that dead end was up for 13 minutes in production.

import type { HandoffState } from './chatBanner';

/**
 * A handoff is ENGAGED once the customer has been sent toward a rep and until
 * it ends — connecting, waiting in queue, or actually talking.
 *
 * `idle` and `ended` are not engaged: there is no rep on the other side, so an
 * overlay costs the customer nothing but the answer they were not getting
 * anyway.
 */
export function handoffEngaged(handoffState: HandoffState): boolean {
  return (
    handoffState === 'connecting' ||
    handoffState === 'queued' ||
    handoffState === 'active'
  );
}

export interface EmergencyOverlayInputs {
  /** announcementMessage — null when there is nothing to announce. */
  message: string | null;
  /** announcementMode — only "emergency" produces the takeover. */
  mode: 'informational' | 'emergency';
  /** announcementDismissed — the dismissal that survives the 15s poll. */
  dismissed: boolean;
  handoffState: HandoffState;
  /**
   * routingPending — the customer asked for a person and the team menu is on
   * screen, but no handoff exists yet. TRAC-1006: part of reaching a person.
   */
  routingPending: boolean;
}

/**
 * The single gate for the full-container emergency takeover.
 *
 * TRAC-975 adds the `handoffEngaged` term. Everything else is TRAC-419 /
 * TRAC-482 behaviour, unchanged.
 *
 * Why the handoff term. The overlay is a blocking layer AND the flag that
 * disables the composer (ChatInputBar's `disabled` + `readOnly`). Dropping it
 * over a live rep conversation switched off the box the customer types in while
 * the rep carried on writing — neither side told why. The agent being sick says
 * nothing about a LivePerson conversation that does not touch the agent, so the
 * overlay has no business appearing over one.
 *
 * Note this is a suppression, not a dismissal: when the handoff ends the
 * overlay comes back if the outage is still on, because at that point it is
 * true again.
 *
 * TRAC-1006 adds `routingPending`. Choosing a team comes BEFORE the handoff
 * exists, so the handoff term never covered it, and the screen could land on
 * the very buttons that lead to a rep. Same kind of term: a suppression that
 * lifts on its own once the menu is answered or cancelled.
 *
 * What this gate cannot do alone is remember any of it across a page change —
 * `handoffState` and `dismissed` are React state and reset on every mount.
 * ChatInterface seeds them from the persisted attempt (services/handoffAttempt)
 * before the first announcement poll can apply, which is the other half of the
 * fix.
 */
export function emergencyOverlayActive({
  message,
  mode,
  dismissed,
  handoffState,
  routingPending,
}: EmergencyOverlayInputs): boolean {
  return (
    message != null &&
    mode === 'emergency' &&
    !dismissed &&
    !handoffEngaged(handoffState) &&
    !routingPending
  );
}

/**
 * What the overlay can honestly offer as a way through to a person.
 *
 * - `live`    → LivePerson is open. Render the action; clicking it escalates.
 * - `offline` → live chat is shut. No button (it would be a lie); the overlay
 *               copy gains the phone/hours block instead.
 * - `none`    → say nothing yet. Either availability is still being checked, or
 *               the copy already carries the phone fallback and repeating it
 *               would just be duplicate bullets on one small card.
 */
export type OverlayHumanRouteKind = 'live' | 'offline' | 'none';

export interface OverlayHumanRouteInputs {
  /** liveChatAvail.state — 'checking' | 'open' | closed_hours | closed_no_agents | … */
  availState: string;
  /**
   * liveChatAvail.failOpen — TRAC-986. True when the backend could not reach
   * LivePerson to ask, and answered `open` as a safe default rather than as a
   * fact. "Assumed open" and "confirmed open" arrive here as the same string,
   * and only this flag tells them apart.
   */
  failOpen?: boolean;
  /**
   * True when the overlay's own copy already lists the phone number, i.e. the
   * retry-exhausted message. A content check, deliberately — the point of this
   * ticket is that keying overlay behaviour off message IDENTITY is what
   * produced the dead end in the first place.
   */
  messageHasPhone: boolean;
}

export interface OverlayHumanRoute {
  kind: OverlayHumanRouteKind;
  /** Button copy — present only for `live`. */
  label?: string;
}

/**
 * The label. Same words as the connect banner's open state, on purpose: this is
 * the same door, and a customer who has seen one should recognise the other.
 */
export const OVERLAY_HUMAN_ACTION_LABEL = 'Talk to a Tire Expert';

export function overlayHumanRoute({
  availState,
  failOpen,
  messageHasPhone,
}: OverlayHumanRouteInputs): OverlayHumanRoute {
  // Still asking LivePerson. Rendering the button now and pulling it a second
  // later is worse than a beat of nothing, and rendering it disabled reads as
  // "unavailable" — which we do not yet know.
  if (availState === 'checking') return { kind: 'none' };

  // TRAC-986: an availability answer of `open` that came from a FAILED check is
  // not an open desk, it is a guess. Offering the button on a guess spends the
  // customer's click, their wait, and their patience, and lands them on the
  // phone number they could have had immediately.
  //
  // It fails at the worst possible moment twice over: the check is most likely
  // to fail during a LivePerson outage, and on THIS screen the agent is already
  // down, so the human route is the only one left.
  //
  // The same distinction is already drawn one layer up — ChatInterface's
  // `a.state === 'open' && !a.failOpen` guard on the live-chat-available
  // analytics event. It just never reached the affordance the customer sees.
  //
  // Deliberately scoped to the overlay. The connect banner's own fail-open
  // behaviour is a broader product call and stays with TRAC-986.
  if (availState === 'open' && !failOpen) {
    return { kind: 'live', label: OVERLAY_HUMAN_ACTION_LABEL };
  }

  // Everything else: closed (hours, no agents), open-but-unconfirmed, or a
  // state this code does not recognise. Either way the honest offer is the
  // phone number, which needs no network and always works.
  return messageHasPhone ? { kind: 'none' } : { kind: 'offline' };
}

/**
 * TRAC-1002: whether the emergency overlay actually blocks the chat.
 *
 * `chat-turns-failing` raises the screen when chat turns fail in both regions
 * and clears it only when turns succeed again. The screen stops customers
 * sending, so the one source of turns left during an outage is the synthetics
 * continuity canary, which loads the chat with `?health_check=true`. If the
 * screen blocked the canary as well, the alarm would have nothing to read.
 *
 * Deliberately a separate step from `emergencyOverlayActive`: that gate decides
 * whether an outage screen is warranted; this one only exempts the health
 * check from it. Anyone who adds the flag by hand skips the screen and simply
 * sees the errors, which is accepted.
 */
export function overlayBlocksChat(overlayActive: boolean, healthCheck: boolean): boolean {
  return overlayActive && !healthCheck;
}
